import type React from 'react';
import { useMemo, useState } from 'react';

import { Switch } from '@headlessui/react';
import type { CookieSelectionType } from '../../../redux/ducks/StorageDucks.types';
import type { CookieServiceDescriptorType } from './CookieSelection.types';

interface CookieSelectionProps {
  preferences: CookieSelectionType;
  setPreferences: (preferences: CookieSelectionType) => void;
}

const CookieSelection: React.FC<CookieSelectionProps> = ({
  preferences,
  setPreferences,
}) => {
  const [moreTechnical, setMoreTechnical] = useState(false);
  const [moreAnalytics, setMoreAnalytics] = useState(false);
  const [morePerformance, setMorePerformance] = useState(false);
  const [moreAdvertisement, setMoreAdvertisement] = useState(false);
  const [moreFunctional, setMoreFunctional] = useState(false);
  const [moreOthers, setMoreOthers] = useState(false);

  const TECHNICAL_COOKIES_DESCRIPTORS: CookieServiceDescriptorType[] = useMemo<
    CookieServiceDescriptorType[]
  >(
    () => [
      {
        title: 'Authorization',
        description: 'Avoids to sign out your account when you leave the page.',
        required: true,
        checked: true,
        onSwitch: () => {},
        cookies: [
          {
            name: 'tenutdomainappauth',
            expiration: '1 year',
            purpose: 'Keeps you authenticated',
            type: 'Cookie Storage',
          },
        ],
      },
      {
        title: 'Preferences',
        description:
          'Saves your preferences for the app, such as cookie preferences and the UI theme (light/dark/auto).',
        required: true,
        checked: true,
        onSwitch: () => {},
        cookies: [
          {
            name: 'persist:blapp_store',
            expiration: 'none',
            purpose: 'Saves your preferences',
            type: 'Local Storage',
          },
        ],
      },
      {
        title: 'Language',
        description: 'Saves your language preferences for the app.',
        required: true,
        checked: true,
        onSwitch: () => {},
        cookies: [
          {
            name: 'i18nextLng',
            expiration: 'none',
            purpose: 'Saves your language preference',
            type: 'Local Storage',
          },
        ],
      },
      {
        title: 'Bugsnag',
        description:
          'BugSnag is a stability monitoring solution that provides error reporting and analytics to reproduce and fix errors.',
        required: true,
        checked: true,
        onSwitch: () => {},
        terms: 'https://www.bugsnag.com/product/security/',
        cookies: [
          {
            name: 'bugsnag-anonymous-id',
            expiration: 'none',
            purpose:
              'This piece of local storage is used to log and notify us of any errors that occur ',
            type: 'Local Storage',
          },
          {
            name: 'bugsnag-sampling-probability',
            expiration: 'none',
            purpose:
              'This cookie has not yet been given a description. Our team is working to provide more information.',
            type: 'Local Storage',
          },
        ],
      },
      {
        title: 'Stripe',
        description:
          'Stripe is a payment processing platform that allows businesses to accept payments from customers through their website, e-commerce platform, app, or pretty much anywhere else on the internet.',
        required: true,
        checked: true,
        onSwitch: () => {},
        terms: 'https://stripe.com/cookie-settings',
        cookies: [
          {
            name: '__stripe_mid',
            expiration: '1 year',
            purpose: 'to provide fraud prevention.',
            type: 'Cookie Storage',
          },
        ],
      },
      {
        title: 'hCaptcha',
        description:
          'hCaptcha helps your favorite online services keep out bots, spam, and abuse by asking simple questions that are easy for humans and difficult for machines.',
        required: true,
        checked: true,
        onSwitch: () => {},
        terms: 'https://www.hcaptcha.com/privacy',
        cookies: [
          {
            name: '__cflb',
            expiration: '1 day',
            purpose: 'to provide bot protection.',
            type: 'Cookie Storage',
          },
          {
            name: 'INGRESSCOOKIE',
            expiration: 'session',
            purpose: 'to provide bot protection.',
            type: 'Session Storage',
          },
        ],
      },
    ],
    [preferences],
  );

  const ANALYTICS_COOKIES_DESCRIPTORS: CookieServiceDescriptorType[] = useMemo<
    CookieServiceDescriptorType[]
  >(() => [], [preferences]);

  const ADVERTISEMENT_COOKIES_DESCRIPTORS: CookieServiceDescriptorType[] = [];
  const PERFORMANCE_COOKIES_DESCRIPTORS: CookieServiceDescriptorType[] = [];
  const FUNCTIONAL_COOKIES_DESCRIPTORS: CookieServiceDescriptorType[] = [
    {
      title: 'OneSignal',
      description:
        'OneSignal is a customer engagement solution for Push Notifications, Email, SMS, In-App Messaging, and Live Activities.',
      required: false,
      checked: preferences?.onesignal || false,
      terms:
        'https://documentation.onesignal.com/docs/data-collected-by-the-onesignal-sdk',
      onSwitch: () => {
        setPreferences({
          ...preferences,
          allFunctional: !preferences.onesignal || preferences.feedbackfish,
          onesignal: !preferences.onesignal,
        });
      },
      cookies: [
        {
          name: 'ONE_SIGNAL_SDK_DB',
          expiration: 'none',
          purpose:
            'Storing user preferences in connection with notification permission status.',
          type: 'IndexedDB dictionary',
        },
        {
          name: 'os_pageViews, isOptedOut, isPushNotificationsEnabled',
          expiration: 'none',
          purpose: 'Prompting and subscription tracking',
          type: 'Local Storage',
        },
        {
          name: 'onesignal-pageview-count, ONESIGNAL_HTTP_PROMPT_SHOWN',
          expiration: 'session',
          purpose: 'Prompting',
          type: 'Session Storage',
        },
      ],
    },
    /*
    {
      title: 'Feedback Fish',
      description:
        'Feedback Fish is a simple widget for collecting website feedback from users: problems, suggestions or anything else.',
      required: false,
      checked: preferences?.feedbackfish || false,
      terms: 'https://feedback.fish/help',
      onSwitch: () => {
        setPreferences({
          ...preferences,
          allFunctional: !preferences.feedbackfish || preferences.onesignal,
          feedbackfish: !preferences.feedbackfish,
        });
      },
      cookies: [
        {
          name: 'theme-ui-color-mode',
          expiration: 'none',
          purpose:
            'This cookie has not yet been given a description. Our team is working to provide more information.',
          type: 'Local Storage',
        },
      ],
    },
    */
  ];
  const OTHER_COOKIES_DESCRIPTORS: CookieServiceDescriptorType[] = [];

  const generateDetailsBlocks = (cookie: CookieServiceDescriptorType) => (
    <div className="mt-4 text-sm">
      <div className="flex flex-row justify-evenly">
        <div className="w-full">
          {cookie?.terms ? (
            <button
              data-test-id={`cookie-btn-item-${cookie.title}`}
              id={`cookie-btn-item-${cookie.title}`}
              key={`cookie-btn-item-${cookie.title}`}
              aria-label={`cookie-btn-item-${cookie.title}`}
              type="button"
              className="cursor-pointer text-sm font-bold underline"
              onClick={() => window.open(cookie.terms, '_blank')}
            >
              {cookie.title}
            </button>
          ) : (
            <span className="text-sm font-bold">{cookie.title}</span>
          )}
        </div>
        <div className="w-full">
          <div className="grid justify-items-end">
            {cookie.required ? (
              <span className="text-xs italic text-gray-600">&nbsp;</span>
            ) : (
              <Switch
                data-test-id={`cookie.switch-all-${cookie.title}`}
                checked={cookie.checked}
                onChange={cookie.onSwitch}
                className={`${
                  cookie.checked ? 'bg-sky-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 items-center rounded-full`}
              >
                <span
                  className={`${
                    cookie.checked ? 'translate-x-6' : 'translate-x-1'
                  } inline-block size-4 rounded-full bg-white transition`}
                />
              </Switch>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 text-xs">
        <span>{cookie.description}</span>
      </div>
      <div className="mt-2 text-xs">
        <table className="w-full table-auto border-collapse border border-gray-100 text-center">
          <thead>
            <tr className="border border-gray-100 bg-gray-100 p-1">
              <th className="border border-gray-100">Name</th>
              <th className="border border-gray-100">Expiration</th>
              <th className="border border-gray-100">Purpose</th>
              <th className="border border-gray-100">Type</th>
            </tr>
          </thead>
          <tbody>
            {cookie.cookies.map((cl) => (
              <tr className="p-1">
                <td className="border border-gray-100 px-1 text-left font-semibold">
                  {cl.name}
                </td>
                <td className="border border-gray-100">{cl.expiration}</td>
                <td className="border border-gray-100 px-1 text-left">
                  {cl.purpose}
                </td>
                <td className="border border-gray-100">{cl.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="flex w-full flex-col">
      <div className="my-1 rounded-lg border-2 border-solid border-gray-100 py-2 pl-4 pr-2">
        <div className="flex flex-row justify-evenly">
          <div className="w-full">
            <span className="text-lg font-bold">Necessary cookies</span>
          </div>
          <div className="w-full">
            <div className="grid justify-items-end">
              <span className="text-sm italic text-gray-600">Required</span>
            </div>
          </div>
        </div>
        <div className="mt-2 text-sm">
          <span>
            Necessary cookies are absolutely essential for the website to
            function properly. This category only includes cookies that ensures
            basic functionalities and security features of the website.{' '}
            {TECHNICAL_COOKIES_DESCRIPTORS.length > 0 ? (
              moreTechnical ? (
                <button
                  aria-label="tech-cookies-less-info-btn"
                  data-test-id="tech-cookies-less-info-btn"
                  id="tech-cookies-less-info-btn"
                  key="tech-cookies-less-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreTechnical(false);
                  }}
                >
                  Less info
                </button>
              ) : (
                <button
                  aria-label="tech-cookies-more-info-btn"
                  data-test-id="tech-cookies-more-info-btn"
                  id="tech-cookies-more-info-btn"
                  key="tech-cookies-more-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreTechnical(true);
                  }}
                >
                  More info
                </button>
              )
            ) : null}
          </span>
        </div>
        {moreTechnical &&
          TECHNICAL_COOKIES_DESCRIPTORS.map((cookie) =>
            generateDetailsBlocks(cookie),
          )}
      </div>
      <div className="my-1 rounded-lg border-2 border-solid border-gray-100 py-2 pl-4 pr-2">
        <div className="flex flex-row justify-evenly">
          <div className="w-full">
            <span className="text-lg font-bold">Analytics</span>
          </div>
          <div className="w-full">
            <div className="grid justify-items-end">
              {!moreAnalytics && (
                <Switch
                  data-test-id="cookie.switch-all-analytics"
                  disabled={ANALYTICS_COOKIES_DESCRIPTORS.length === 0}
                  checked={
                    preferences.allAnalytics &&
                    ANALYTICS_COOKIES_DESCRIPTORS.length > 0
                  }
                  onChange={() => {
                    setPreferences({
                      ...preferences,
                      allAnalytics: !preferences.allAnalytics,
                    });
                  }}
                  className={`${
                    preferences.allAnalytics &&
                    ANALYTICS_COOKIES_DESCRIPTORS.length > 0
                      ? 'bg-sky-600'
                      : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${
                      preferences.allAnalytics &&
                      ANALYTICS_COOKIES_DESCRIPTORS.length > 0
                        ? 'translate-x-6'
                        : 'translate-x-1'
                    } inline-block size-4 rounded-full bg-white transition`}
                  />
                </Switch>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2 text-sm">
          <span>
            Analytical cookies are used to understand how visitors interact with
            the website. These cookies help provide information on metrics the
            number of visitors, bounce rate, traffic source, etc.{' '}
            {ANALYTICS_COOKIES_DESCRIPTORS.length > 0 ? (
              moreAnalytics ? (
                <button
                  aria-label="analytics-cookies-less-info-btn"
                  data-test-id="analytics-cookies-less-info-btn"
                  id="analytics-cookies-less-info-btn"
                  key="analytics-cookies-less-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreAnalytics(false);
                  }}
                >
                  Less info
                </button>
              ) : (
                <button
                  aria-label="analytics-cookies-more-info-btn"
                  data-test-id="analytics-cookies-more-info-btn"
                  id="analytics-cookies-more-info-btn"
                  key="analytics-cookies-more-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreAnalytics(true);
                  }}
                >
                  More info
                </button>
              )
            ) : null}
          </span>
        </div>
        {moreAnalytics &&
          ANALYTICS_COOKIES_DESCRIPTORS.map((cookie) =>
            generateDetailsBlocks(cookie),
          )}
      </div>
      <div className="my-1 rounded-lg border-2 border-solid border-gray-100 py-2 pl-4 pr-2">
        <div className="flex flex-row justify-evenly">
          <div className="w-full">
            <span className="text-lg font-bold">Performance</span>
          </div>
          <div className="w-full">
            <div className="grid justify-items-end">
              {!morePerformance && (
                <Switch
                  data-test-id="cookie.switch-all-performance"
                  disabled={PERFORMANCE_COOKIES_DESCRIPTORS.length === 0}
                  checked={
                    preferences.allPerformance &&
                    PERFORMANCE_COOKIES_DESCRIPTORS.length > 0
                  }
                  onChange={() => {
                    setPreferences({
                      ...preferences,
                      allPerformance: !preferences.allPerformance,
                    });
                  }}
                  className={`${
                    preferences.allPerformance &&
                    PERFORMANCE_COOKIES_DESCRIPTORS.length > 0
                      ? 'bg-sky-600'
                      : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${
                      preferences.allPerformance &&
                      PERFORMANCE_COOKIES_DESCRIPTORS.length > 0
                        ? 'translate-x-6'
                        : 'translate-x-1'
                    } inline-block size-4 rounded-full bg-white transition`}
                  />
                </Switch>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2 text-sm">
          <span>
            Performance cookies are used to understand and analyze the key
            performance indexes of the website which helps in delivering a
            better user experience for the visitors.{' '}
            {PERFORMANCE_COOKIES_DESCRIPTORS.length > 0 ? (
              morePerformance ? (
                <button
                  aria-label="performance-cookies-less-info-btn"
                  data-test-id="performance-cookies-less-info-btn"
                  id="performance-cookies-less-info-btn"
                  key="performance-cookies-less-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMorePerformance(false);
                  }}
                >
                  Less info
                </button>
              ) : (
                <button
                  aria-label="performance-cookies-more-info-btn"
                  data-test-id="performance-cookies-more-info-btn"
                  id="performance-cookies-more-info-btn"
                  key="performance-cookies-more-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMorePerformance(true);
                  }}
                >
                  More info
                </button>
              )
            ) : null}
          </span>
        </div>
        {morePerformance &&
          PERFORMANCE_COOKIES_DESCRIPTORS.map((cookie) =>
            generateDetailsBlocks(cookie),
          )}
      </div>
      <div className="my-1 rounded-lg border-2 border-solid border-gray-100 py-2 pl-4 pr-2">
        <div className="flex flex-row justify-evenly">
          <div className="w-full">
            <span className="text-lg font-bold">Advertisement</span>
          </div>
          <div className="w-full">
            <div className="grid justify-items-end">
              {!moreAdvertisement && (
                <Switch
                  data-test-id="cookie.switch-all-advertisement"
                  disabled={ADVERTISEMENT_COOKIES_DESCRIPTORS.length === 0}
                  checked={
                    preferences.allAdvertisement &&
                    ADVERTISEMENT_COOKIES_DESCRIPTORS.length > 0
                  }
                  onChange={() => {
                    setPreferences({
                      ...preferences,
                      allAdvertisement: !preferences.allAdvertisement,
                    });
                  }}
                  className={`${
                    preferences.allAdvertisement &&
                    ADVERTISEMENT_COOKIES_DESCRIPTORS.length > 0
                      ? 'bg-sky-600'
                      : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${
                      preferences.allAdvertisement &&
                      ADVERTISEMENT_COOKIES_DESCRIPTORS.length > 0
                        ? 'translate-x-6'
                        : 'translate-x-1'
                    } inline-block size-4 rounded-full bg-white transition`}
                  />
                </Switch>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2 text-sm">
          <span>
            Advertisement cookies are used to provide visitors with relevant ads
            and marketing campaigns. These cookies track visitors across
            websites and collect information to provide customized ads.{' '}
            {ADVERTISEMENT_COOKIES_DESCRIPTORS.length > 0 ? (
              moreAdvertisement ? (
                <button
                  aria-label="advertisement-cookies-more-info-btn"
                  data-test-id="advertisement-cookies-more-info-btn"
                  id="advertisement-cookies-more-info-btn"
                  key="advertisement-cookies-more-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreAdvertisement(false);
                  }}
                >
                  Less info
                </button>
              ) : (
                <button
                  aria-label="advertisement-cookies-more-info-btn"
                  data-test-id="advertisement-cookies-more-info-btn"
                  id="advertisement-cookies-more-info-btn"
                  key="advertisement-cookies-more-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreAdvertisement(true);
                  }}
                >
                  More info
                </button>
              )
            ) : null}
          </span>
        </div>
        {moreAdvertisement &&
          ADVERTISEMENT_COOKIES_DESCRIPTORS.map((cookie) =>
            generateDetailsBlocks(cookie),
          )}
      </div>
      <div className="my-1 rounded-lg border-2 border-solid border-gray-100 py-2 pl-4 pr-2">
        <div className="flex flex-row justify-evenly">
          <div className="w-full">
            <span className="text-lg font-bold">Functional</span>
          </div>
          <div className="w-full">
            <div className="grid justify-items-end">
              {!moreFunctional && (
                <Switch
                  data-test-id="cookie.switch-all-functional"
                  disabled={FUNCTIONAL_COOKIES_DESCRIPTORS.length === 0}
                  checked={
                    preferences.allFunctional &&
                    FUNCTIONAL_COOKIES_DESCRIPTORS.length > 0
                  }
                  onChange={() => {
                    setPreferences({
                      ...preferences,
                      allFunctional: !preferences.allFunctional,
                      onesignal: !preferences.allFunctional,
                      feedbackfish: !preferences.allFunctional,
                    });
                  }}
                  className={`${
                    preferences.allFunctional &&
                    FUNCTIONAL_COOKIES_DESCRIPTORS.length > 0
                      ? 'bg-sky-600'
                      : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${
                      preferences.allFunctional &&
                      FUNCTIONAL_COOKIES_DESCRIPTORS.length > 0
                        ? 'translate-x-6'
                        : 'translate-x-1'
                    } inline-block size-4 rounded-full bg-white transition`}
                  />
                </Switch>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2 text-sm">
          <span>
            Functional cookies help to perform certain functionalities like
            sharing the content of the website on social media platforms,
            collect feedbacks, and other third-party features.{' '}
            {FUNCTIONAL_COOKIES_DESCRIPTORS.length > 0 ? (
              moreFunctional ? (
                <button
                  aria-label="functional-cookies-less-info-btn"
                  data-test-id="functional-cookies-less-info-btn"
                  id="functional-cookies-less-info-btn"
                  key="functional-cookies-less-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreFunctional(false);
                  }}
                >
                  Less info
                </button>
              ) : (
                <button
                  aria-label="functional-cookies-more-info-btn"
                  data-test-id="functional-cookies-more-info-btn"
                  id="functional-cookies-more-info-btn"
                  key="functional-cookies-more-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreFunctional(true);
                  }}
                >
                  More info
                </button>
              )
            ) : null}
          </span>
        </div>
        {moreFunctional &&
          FUNCTIONAL_COOKIES_DESCRIPTORS.map((cookie) =>
            generateDetailsBlocks(cookie),
          )}
      </div>
      <div className="my-1 rounded-lg border-2 border-solid border-gray-100 py-2 pl-4 pr-2">
        <div className="flex flex-row justify-evenly">
          <div className="w-full">
            <span className="text-lg font-bold">Others</span>
          </div>
          <div className="w-full">
            <div className="grid justify-items-end">
              {!moreOthers && (
                <Switch
                  data-test-id="cookie.switch-all-others"
                  disabled={OTHER_COOKIES_DESCRIPTORS.length === 0}
                  checked={
                    preferences.allOthers &&
                    OTHER_COOKIES_DESCRIPTORS.length > 0
                  }
                  onChange={() => {
                    setPreferences({
                      ...preferences,
                      allOthers: !preferences.allOthers,
                    });
                  }}
                  className={`${
                    preferences.allOthers &&
                    OTHER_COOKIES_DESCRIPTORS.length > 0
                      ? 'bg-sky-600'
                      : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${
                      preferences.allOthers &&
                      OTHER_COOKIES_DESCRIPTORS.length > 0
                        ? 'translate-x-6'
                        : 'translate-x-1'
                    } inline-block size-4 rounded-full bg-white transition`}
                  />
                </Switch>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2 text-sm">
          <span>
            Other uncategorized cookies are those that are being analyzed and
            have not been classified into a category as yet.{' '}
            {OTHER_COOKIES_DESCRIPTORS.length > 0 ? (
              moreOthers ? (
                <button
                  aria-label="other-cookies-less-info-btn"
                  data-test-id="other-cookies-less-info-btn"
                  id="other-cookies-less-info-btn"
                  key="other-cookies-less-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreOthers(false);
                  }}
                >
                  Less info
                </button>
              ) : (
                <button
                  aria-label="other-cookies-more-info-btn"
                  data-test-id="other-cookies-more-info-btn"
                  id="other-cookies-more-info-btn"
                  key="other-cookies-more-info-btn"
                  type="button"
                  className="cursor-pointer text-sky-600 hover:text-sky-400"
                  onClick={() => {
                    setMoreOthers(true);
                  }}
                >
                  More info
                </button>
              )
            ) : null}
          </span>
        </div>
        {moreOthers &&
          OTHER_COOKIES_DESCRIPTORS.map((cookie) =>
            generateDetailsBlocks(cookie),
          )}
      </div>
    </div>
  );
};

export default CookieSelection;
